import OutlookRestRequest from './request'
import { isDesktopMac, isWeb } from '../client'
import { addBreadcrumb, captureException } from '../../sentry-config'

export default class OutlookRest {
  constructor(office) {
    this.office = office
    this.api = new OutlookRestRequest(office)
  }

  isSupported = () =>
    this.office.context.requirements.isSetSupported('Mailbox', '1.5') &&
    (isWeb(this.office) || isDesktopMac(this.office))
  isValidResponse = response =>
    response && response.ok && response.headers.get('content-type').includes('application/json')

  getCurrentMessage = async (restId, isNestedAppAuthEnabled = false) => {
    if (!this.isSupported()) {
      addBreadcrumb({ message: 'Rest: not supported', category: 'office-js' })
      return false
    }

    try {
      const response = await this.api.get(
        `me/messages/${restId}/?$expand=attachments($select=IsInline)`,
        {},
        isNestedAppAuthEnabled
      )
      if (this.isValidResponse(response)) {
        addBreadcrumb({ message: 'Rest: received a message', category: 'ms-graph-api' })
        const message = await response.json()
        const attachments = await this.fetchInlineAttachments(
          restId,
          isNestedAppAuthEnabled ? message.attachments : message.Attachments
        )

        return {
          subject: isNestedAppAuthEnabled ? message.subject : message.Subject,
          body: isNestedAppAuthEnabled ? message.body.content : message.Body.Content,
          attachments,
        }
      }
    } catch (error) {
      captureException(new Error(error.name || 'OOutlookRest#getCurrentMessage'), {
        fingerprint: ['OutlookRest#getCurrentMessage'],
        extra: {
          message: error.message,
          code: error.code,
          traceMessages: error.traceMessages || [],
        },
      })
      return false
    }

    return false
  }

  fetchInlineAttachments = async (restId, attachments) => {
    let resolvedAttachments = []
    for (const attachment of attachments.filter(attachment => attachment.IsInline)) {
      resolvedAttachments.push(await this.getAttachmentById(restId, attachment.Id))
    }

    return resolvedAttachments
  }

  getAttachmentById = async (restId, attachmentId) => {
    const selectFields = [
      'Name',
      'IsInline',
      'Microsoft.OutlookServices.FileAttachment/ContentId',
      'Microsoft.OutlookServices.FileAttachment/ContentBytes',
    ].join(',')
    const response = await this.api.get(
      `me/messages/${restId}/attachments/${attachmentId}/?$select=${selectFields}`
    )
    if (this.isValidResponse(response)) {
      addBreadcrumb({ message: 'Rest: received attachment', category: 'ms-graph-api' })
      return response.json()
    }
    return false
  }
}
